// The site's QR code on screen, and as a picture file to download. The squares come
// from the same code as the printed poster, so all three always match.

import { qrModules, QUIET_ZONE } from '../../src/poster.ts';

function qrPath(text: string): { size: number; path: string } {
  const modules = qrModules(text);
  let path = '';
  modules.forEach((row, r) => {
    for (let c = 0; c < row.length; c++) {
      if (!row[c]) continue;
      let end = c;
      while (end + 1 < row.length && row[end + 1]) end++;
      path += `M${c + QUIET_ZONE} ${r + QUIET_ZONE}h${end - c + 1}v1h-${end - c + 1}z`;
      c = end;
    }
  });
  return { size: modules.length + 2 * QUIET_ZONE, path };
}

export function QRCode({ text, px, label }: { text: string; px: number; label: string }) {
  const { size, path } = qrPath(text);
  return (
    <svg width={px} height={px} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label} shapeRendering="crispEdges" style={{ display: 'block' }}>
      <rect width={size} height={size} fill="#FFFFFF" />
      <path d={path} fill="#111316" />
    </svg>
  );
}

/** The QR code as a PNG, about 1200 pixels across: fine for posters, slides and documents. */
export async function qrPng(text: string): Promise<Blob> {
  const modules = qrModules(text);
  const count = modules.length + 2 * QUIET_ZONE;
  const cell = Math.ceil(1200 / count);
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = count * cell;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error("This browser can't make the picture.");
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#111316';
  modules.forEach((row, r) => row.forEach((dark, c) => dark && ctx.fillRect((c + QUIET_ZONE) * cell, (r + QUIET_ZONE) * cell, cell, cell)));
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("The picture couldn't be made."))), 'image/png'));
}

/** Saves a file made in the page (nothing is sent anywhere). */
export function saveFile(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
